// src/utils/toast.ts
import { toast as hotToast } from 'react-hot-toast'
import type { ToastOptions, Renderable, ValueFunction, Toast } from 'react-hot-toast'

/**
 * Format any ISO date or date-time string to Indian Standard Time (IST) with AM/PM.
 * Example: '2026-10-09T07:15:00Z' -> '09 Oct 2026, 12:45 PM'
 * Example: '2026-10-10T00:00:00Z' -> '10 Oct 2026, 05:30 AM'
 */
export function formatToIndianTime(dateInput: string | Date): string {
  if (!dateInput) return ''
  try {
    const isString = typeof dateInput === 'string'
    const hasTime = isString ? /[T\s]\d{1,2}:\d{2}/.test(dateInput) : true
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
    if (isNaN(d.getTime())) return typeof dateInput === 'string' ? dateInput : ''

    if (hasTime) {
      const parts = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).formatToParts(d)

      const partObj: Record<string, string> = {}
      parts.forEach((p) => {
        partObj[p.type] = p.value
      })

      const day = partObj['day'] || ''
      const month = partObj['month'] || ''
      const year = partObj['year'] || ''
      const hour = partObj['hour'] || ''
      const minute = partObj['minute'] || ''
      const dayPeriod = (partObj['dayPeriod'] || '').toUpperCase()

      return `${day} ${month} ${year}, ${hour}:${minute} ${dayPeriod}`
    } else {
      const parts = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).formatToParts(d)

      const partObj: Record<string, string> = {}
      parts.forEach((p) => {
        partObj[p.type] = p.value
      })

      const day = partObj['day'] || ''
      const month = partObj['month'] || ''
      const year = partObj['year'] || ''

      return `${day} ${month} ${year}`
    }
  } catch {
    return typeof dateInput === 'string' ? dateInput : ''
  }
}

/**
 * Replace all ISO date and date-time occurrences in a message string with Indian Time (AM/PM).
 */
export function replaceIsoDatesWithIndianTime(message: string): string {
  if (!message || typeof message !== 'string') return message

  // Match ISO timestamps like 2026-10-09T07:15:00Z, 2026-10-09T07:15:00.000Z, 2026-10-09T07:15:00+05:30, 2026-10-09T07:15:00
  const isoDateTimeRegex = /\b\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\b/g

  return message.replace(isoDateTimeRegex, (match) => {
    return formatToIndianTime(match)
  })
}

/**
 * Transform renderable content (string or function) to format embedded ISO timestamps to Indian time.
 */
const transformRenderable = (
  message: Renderable | ValueFunction<Renderable, Toast>
): Renderable | ValueFunction<Renderable, Toast> => {
  if (typeof message === 'string') {
    return replaceIsoDatesWithIndianTime(message)
  }
  if (typeof message === 'function') {
    return (t: Toast) => {
      const res = message(t)
      if (typeof res === 'string') {
        return replaceIsoDatesWithIndianTime(res)
      }
      return res
    }
  }
  return message
}

/**
 * Get a unique ID for a toast based on its message content to prevent duplicates.
 */
const getToastId = (message: Renderable | ValueFunction<Renderable, Toast>): string | undefined => {
  if (typeof message === 'string') {
    return message
  }
  if (typeof message === 'number') {
    return String(message)
  }
  return undefined
}

/**
 * Build the ToastOptions configuration object, avoiding explicit undefined values
 * to satisfy strict exactOptionalPropertyTypes compilation.
 */
const getToastConfig = (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions): ToastOptions => {
  const config: ToastOptions = { ...options }
  const id = options?.id ?? getToastId(message)
  if (id !== undefined) {
    config.id = id
  }
  return config
}

interface CustomToast {
  (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions): string
  success: (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => string
  error: (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => string
  loading: (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => string
  dismiss: (id?: string) => void
}

const customToastFn = (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => {
  const formattedMessage = transformRenderable(message)
  return hotToast(formattedMessage, getToastConfig(formattedMessage, options))
}

customToastFn.success = (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => {
  const formattedMessage = transformRenderable(message)
  return hotToast.success(formattedMessage, getToastConfig(formattedMessage, options))
}

customToastFn.error = (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => {
  const formattedMessage = transformRenderable(message)
  return hotToast.error(formattedMessage, getToastConfig(formattedMessage, options))
}

customToastFn.loading = (message: Renderable | ValueFunction<Renderable, Toast>, options?: ToastOptions) => {
  const formattedMessage = transformRenderable(message)
  return hotToast.loading(formattedMessage, getToastConfig(formattedMessage, options))
}

customToastFn.dismiss = (id?: string) => {
  hotToast.dismiss(id)
}

export const toast = customToastFn as CustomToast
export default toast
